import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType,
} from '@appblocks/codegeneration/types';

export default class Crontab extends BaseFeature {
    constructor() {
        super();
        this.name = 'Schedule';
        this.id = 'crontab';
        this.singleInstance = false;
        this.linkedFeature = 'cron';
        this.settings = this.settings.concat([
            {
                id: 'NAME',
                name: 'Schedule Name',
                settingType: SettingType.NAME,
                required: true,
                default: 'Schedule',
            },
            {
                id: 'PATTERN',
                name: 'Expression',
                settingType: SettingType.CRON,
                required: true,
                default: '* * * * * *',
            },
        ]);
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'NAME');
    }

    getDaysInMonth(month: number) {
        const year = new Date().getFullYear();
        if (month === 2) {
            return 29;
        }
        return new Date(year, month, 0).getDate();
    }

    cronParser(pattern:string) : string {
        const time = (hour:string, minute:string) : string => {
            minute = minute.padStart(2, '0');
            const isAM = Number(hour) < 12;
            if (hour === '0') hour = '12';
            return `${isAM || hour === '12' ? hour : Number(hour) - 12}:${minute} ${isAM ? 'AM' : 'PM'}`;
        };
        let sentence:string = '';
        const patternParts: string[] = pattern.split(' ');
        if (patternParts[5] !== '*') {
            switch (patternParts[5]) {
                case 'r':
                    sentence += 'On sunrise';
                    break;
                case 's':
                    sentence += 'On sunset';
                    break;
                case 'rs':
                    sentence += 'On sunrise and sunset';
                    break;
                default:
                    break;
            }
        } else {
            // eslint-disable-next-line no-lonely-if
            if (patternParts[0] + patternParts[1] === '**') {
                sentence += 'At every minute';
            } else if (patternParts[1] === '*') {
                sentence += `At minute ${patternParts[0]} past every hour`;
            } else if (patternParts[0] === '*') {
                sentence += `At every minute past ${time(patternParts[1], '0')}`;
            } else {
                sentence += `At ${time(patternParts[1], patternParts[0])}`;
            }
        }
        if (patternParts[2] !== '*' && patternParts[3] !== '*') {
            sentence += ` on ${months[Number(patternParts[3]) - 1].slice(0, 3)} ${patternParts[2]}`;
        } else if (patternParts[2] === '*' && patternParts[3] !== '*') {
            sentence += ` in ${months[Number(patternParts[3]) - 1].slice(0, 3)}`;
        } else if (patternParts[3] === '*' && patternParts[2] !== '*') {
            sentence += ` on day ${patternParts[2]} of every month`;
        } else {
            sentence += '';
        }
        if (patternParts[4] === '*' || patternParts[4].length === 0) {
            sentence += '';
        } else if (patternParts[4].length === 1) {
            sentence += ` and on ${week[Number(patternParts[4])].slice(0, 3)}`;
        } else {
            sentence += ' and on';
            const weekdays = patternParts[4].split(',');
            for (let i = 0; i < weekdays.length - 1; i++) {
                sentence += ` ${week[Number(weekdays[i])].slice(0, 3)},`;
            }
            sentence += ` and ${week[Number(weekdays[weekdays.length - 1])].slice(0, 3)}`;
        }
        return sentence;
    }

    validate(): Array<ValidationMessage> {
        const errors: ValidationMessage[] = [];
        const parts = this.getSettingValue('PATTERN').split(' ');
        const daysInMonth = this.getDaysInMonth(Number(parts[3]));
        if (parts[2] !== '*' && Number(parts[2]) > daysInMonth) {
            errors.push({
                message: `Invalid day of month: ${parts[2]}`,
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
                settingId: 'PATTERN',
            });
        }
        if (parts[4] === '') {
            parts[4] = '*';
            errors.push({
                messageType: ValidationMessageType.UPDATE,
                message: '',
                featureId: this.getID(),
                settingId: 'PATTERN',
                data: parts.join(' '),
            });
        }
        return errors;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];
        const NAME = this.getSettingValue('NAME');

        if (this.project.runtime === 'simulator') {
            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                template: `
function cron_expired_${NAME}() {
    /****cron_expired_${NAME}****/
    trigger_cron_${NAME}_timer();
}
`,
            });

            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
let cron_${NAME}_timer;`,
            });

            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                template: `
function trigger_cron_${NAME}_timer() {
    const nextCronTime = getNextEvent('${this.getSettingValue('PATTERN')}');
    const nextCronTs = nextCronTime.getTime();
    console.log(\`Next scheduled ${NAME} : \${formatTimestamp(nextCronTs / 1000, 'YYYY-MM-DD HH:mm:ss')}\`);
    const localNow = datetimeUTCToLocal(Date.now() / 1000);
    cron_${NAME}_timer = setTimeout(() => {
        cron_expired_${NAME}();
    }, (nextCronTs - (localNow * 1000)));
}
`,
            });

            blocks.push({
                location: 'boot_complete',
                blockType: BlockType.CODE,
                template: `
trigger_cron_${NAME}_timer();`,
            });


            return blocks;
        }

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare sub cron_expired_${NAME}()`,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub cron_expired_${NAME}()
    '****cron_expired_${NAME}****'
end sub`,
        });

        return blocks;
    }
}
const months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'];
const week: string[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'];
